export const BASE64_STANDARD = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
export const BASE64_URL = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
export const BASE32_STANDARD = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
const BASE32_HEX = "0123456789ABCDEFGHIJKLMNOPQRSTUV";
const BASE32_CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

export const BASE32_ALPHABETS: Record<string, string> = {
  rfc4648: BASE32_STANDARD,
  "rfc4648-nopad": BASE32_STANDARD,
  base32hex: BASE32_HEX,
  crockford: BASE32_CROCKFORD,
};

function buildLookup(alphabet: string, aliases: Record<string, number> = {}): Map<string, number> {
  const lookup = new Map<string, number>();
  for (let i = 0; i < alphabet.length; i++) {
    lookup.set(alphabet[i], i);
  }
  for (const [char, value] of Object.entries(aliases)) {
    lookup.set(char, value);
  }
  return lookup;
}

// Both alphabets share the first 62 characters, so accepting all four tail characters decodes either spelling.
export const BASE64_LOOKUP = buildLookup(BASE64_STANDARD, { "-": 62, _: 63 });

export function base32Lookup(variant: string): Map<string, number> {
  const alphabet = BASE32_ALPHABETS[variant] ?? BASE32_STANDARD;
  if (variant !== "crockford") return buildLookup(alphabet);
  // Crockford drops I, L, O and U to survive transcription; they decode as the digits they resemble.
  return buildLookup(alphabet, { I: 1, L: 1, O: 0 });
}

// Both alphabets are a power of two long, so `length - 1` is the bit mask for one output character.
export function encodeWithAlphabet(bytes: Uint8Array, alphabet: string, bitsPerChar: number): string {
  const mask = alphabet.length - 1;
  let out = "";
  let buffer = 0;
  let bits = 0;

  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= bitsPerChar) {
      bits -= bitsPerChar;
      out += alphabet[(buffer >> bits) & mask];
    }
  }
  if (bits > 0) {
    out += alphabet[(buffer << (bitsPerChar - bits)) & mask];
  }
  return out;
}

export function decodeWithAlphabet(
  chars: string,
  lookup: Map<string, number>,
  bitsPerChar: number,
): Uint8Array<ArrayBuffer> {
  const out: number[] = [];
  let buffer = 0;
  let bits = 0;

  for (const char of chars) {
    const value = lookup.get(char);
    if (value === undefined) {
      throw new Error(`"${char}" is not valid for the selected format`);
    }
    buffer = (buffer << bitsPerChar) | value;
    bits += bitsPerChar;
    if (bits >= 8) {
      bits -= 8;
      out.push((buffer >> bits) & 0xff);
    }
  }
  // Anything left over should be padding bits only; a whole unused character means the input was truncated.
  if (bits >= bitsPerChar) {
    throw new Error("Input is truncated: it does not contain a whole number of bytes");
  }
  return new Uint8Array(out);
}

export function pad(encoded: string, blockSize: number): string {
  const remainder = encoded.length % blockSize;
  return remainder === 0 ? encoded : encoded + "=".repeat(blockSize - remainder);
}

// The hex spelling is read the same way wherever it is typed, which is the input itself and an XOR key alike.
export function hexToBytes(text: string, subject: string): Uint8Array<ArrayBuffer> {
  const cleaned = text.replace(/0x/gi, "").replace(/[\s,:_-]+/g, "");
  if (!/^[0-9a-f]*$/i.test(cleaned)) {
    throw new Error(`${subject} contains characters that are not hexadecimal digits`);
  }
  if (cleaned.length % 2 !== 0) {
    throw new Error(`${subject} must have an even number of hexadecimal digits`);
  }
  const bytes = new Uint8Array(cleaned.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(cleaned.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}
